import { message } from 'antd';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useGitLabGroups from '../../../hooks/query/useGitLabGroups';
import useGitLabSubgroups from '../../../hooks/query/useGitLabSubgroups';

export const PATH_NAME_PATTERN = RegExp(/^[a-z0-9_-]+$/);

export const PATH_NAME_PATTERN_INVALID_GROUP_MESSAGE =
  'New groups can only contain lowercase letters, numbers, hyphens, and underscores.';

export const PATH_NAME_PATTERN_INVALID_PROJECT_NAME_MESSAGE =
  'Project name can only contain lowercase letters, numbers, hyphens, and underscores.';

type GitLabGroupOption = {
  value: string;
  label: string;
  type: 'group';
  disabled?: boolean;
  projectCreationForbidden?: boolean;
};

type NewGitLabGroupOption = {
  value: string;
  label: string;
  type: 'new-group';
  disabled?: boolean;
};

export type Option = GitLabGroupOption | NewGitLabGroupOption;

function useOptions() {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [value, setValue] = useState<string[]>([]);

  const [allOptionsByIds] = useState<{ [id: string]: Option }>({});

  const parentGroupId = value[value.length - 1];
  const lastSelectedOption = parentGroupId
    ? allOptionsByIds[parentGroupId]
    : undefined;

  const rootGroupsQuery = useGitLabGroups(!parentGroupId);
  const subGroupsQuery = useGitLabSubgroups(
    parentGroupId,
    lastSelectedOption?.type === 'group',
  );

  const {
    isLoading,
    data: groups,
    error,
  } = parentGroupId ? subGroupsQuery : rootGroupsQuery;

  useEffect(() => {
    if (error) {
      message.error('Failed to load GitLab groups');
      console.error(error);
    }
  }, [error]);

  let options: Option[] = [];

  if (groups) {
    options = groups.map((group) => ({
      value: group.id,
      label: group.path,
      type: 'group',
      projectCreationForbidden: group.projectCreationForbidden,
    }));
  }

  options.forEach((option) => {
    allOptionsByIds[option.value] = option;
  });

  const canAddNewGroup = value.length > 0; // cannot add top-level groups

  const filteredGroupOptions = options.filter((option) => {
    if (!searchTerm) {
      return true;
    }
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const availableOptions = filteredGroupOptions;

  const findGroupBySearch = () =>
    filteredGroupOptions.find(
      (o) => o.label.toLowerCase() === searchTerm?.toLowerCase(),
    );

  const searchTermError =
    searchTerm && canAddNewGroup && !PATH_NAME_PATTERN.test(searchTerm)
      ? PATH_NAME_PATTERN_INVALID_GROUP_MESSAGE
      : undefined;

  const parentGroupSelected = value.length > 0;

  if (
    !isLoading &&
    searchTerm &&
    !findGroupBySearch() &&
    parentGroupSelected &&
    !searchTermError
  ) {
    const newGroupOption: Option = {
      value: uuidv4(),
      label: `${searchTerm}`,
      type: 'new-group',
      disabled: !!searchTermError,
    };

    availableOptions.unshift(newGroupOption);
    allOptionsByIds[newGroupOption.value] = newGroupOption;
  }

  const selectedOptions = value.map((id) => allOptionsByIds[id]);

  const onSearch = setSearchTerm;

  const onChange = (value: string[]) => {
    setValue(value);
    setSearchTerm(undefined);
  };

  return {
    isLoading,
    availableOptions,
    selectedOptions,
    allOptionsByIds,
    value,
    onSearch,
    searchTerm,
    onChange,
    searchTermError,
    canAddNewGroup,
  };
}

export default useOptions;
